// Third Party
import {
  NextPage,
  InferGetStaticPropsType,
  GetStaticPaths,
  GetStaticPropsContext,
} from 'next';
import { useSession } from 'next-auth/react';

// Utils
import { canonicalUrlForPath } from '@routes';
import { PropertyType, SearchPageContentType } from '@gql';
import {
  imperativeNodeListingsByPlaceNameQuery,
  imperativeNodeSearchPageContentForUrlPathAndTypeQuery,
} from '@utils/imperative-node-queries';

// UI
import { PublicLayout } from '@layouts/public-layout';
import { SeoSerpPage } from '@pages-components/seo-serp-page';

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  return {
    paths: [], //indicates that no page needs be created at build time
    fallback: 'blocking', //indicates the type of fallback
  };
};

export async function getStaticProps(context: GetStaticPropsContext) {
  const pathPlaceName = context.params?.place;

  let urlPath = '';
  if (Array.isArray(pathPlaceName)) urlPath = '/' + pathPlaceName.join('/');
  else if (typeof pathPlaceName === 'string') urlPath = '/' + pathPlaceName;
  else throw new Error('urlPath must be a string');

  const result = await Promise.all([
    await imperativeNodeSearchPageContentForUrlPathAndTypeQuery({
      variables: {
        urlPath: urlPath,
        searchType: SearchPageContentType.VacationRental,
      },
      errorPolicy: 'all',
    }),
    await imperativeNodeListingsByPlaceNameQuery({
      variables: {
        propertyTypes: [
          PropertyType.House,
          PropertyType.GuestHouse,
          PropertyType.Apartment,
          PropertyType.BoutiqueHotel,
        ],
        regionPath: urlPath,
        // this needs to be edited before release because it will not be reliable until it is mapped to the region then mapped to the place id
        pageSize: 10,
      },
    }),
  ])
    .then(
      ([
        {
          data: { searchPageContentForUrlPathAndType },
        },
        { data: regionData },
      ]) => {
        if (
          searchPageContentForUrlPathAndType.__typename !== 'SearchPageContent'
        ) {
          return {
            notFound: true,
            props: undefined,
          };
        } else {
          return {
            props: {
              urlPath: urlPath,
              searchPageContent: searchPageContentForUrlPathAndType,
              regionDataEntries: regionData.regionListingsCollection.entries,
            },
          };
        }
      }
    )
    .catch(() => {
      return {
        notFound: true,
        props: undefined,
      };
    });

  if (result.props) {
    return {
      props: result.props,
      // cache page for 24 hours
      revalidate: 86400,
    };
  }

  return {
    notFound: true,
    // if there is not a search page content for the url path and type
    // we don't want a cached page to stop a new one from being generated
    revalidate: 30,
  };
}

const SearchPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  searchPageContent,
  urlPath,
  regionDataEntries,
}) => {
  const { data: session, status } = useSession();
  return (
    <PublicLayout
      session={session}
      loadingSession={status === 'loading'}
      canonicalUrl={canonicalUrlForPath('/vacation-rentals' + urlPath)}
      pageDescription={
        searchPageContent?.htmlMetaDescription ||
        `Search for a Vacation Rentals near ${
          searchPageContent?.region?.description || ''
        }. Find the Best Vacation Rentals near ${
          searchPageContent?.region?.description || ''
        } on Savvy the Worlds Best Vacation Rentals Directory.`
      }
      pageTitle={
        searchPageContent?.htmlMetaTitle ||
        `Search for ${
          searchPageContent?.region?.description || ''
        } Vacation Rentals | Savvy`
      }
      imageUrl={searchPageContent?.heroImageUrl}
    >
      <SeoSerpPage
        searchPageContent={searchPageContent || undefined}
        regionDataEntries={regionDataEntries}
      />
    </PublicLayout>
  );
};

export default SearchPage;
